import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import useRealtimeSubscription from '../../hooks/useRealtimeSubscription';
import Icon from '../../components/AppIcon';

import AdminNotificationProvider, { useAdminNotifications } from '../../contexts/AdminNotificationContext';
import AdminSidebar from './components/AdminSidebar';
import DashboardOverview from './components/DashboardOverview';
import UserManagement from './components/UserManagement';
import BrandManagement from './components/BrandManagement';
import CampaignManagement from './components/CampaignManagement';
import ModerationQueue from './components/ModerationQueue';
import RewardsLedger from './components/RewardsLedger';
import AnalyticsReports from './components/AnalyticsReports';
import NotificationsCommunications from './components/NotificationsCommunications';
import PlatformSettings from './components/PlatformSettings';
import AuditLogs from './components/AuditLogs';
import ToastContainer from './components/ToastContainer';
import NotificationBell from './components/NotificationBell';
import CardApplicationsReview from './components/CardApplicationsReview';

const AdminPanelInner = () => {
  const { user, userProfile, signOut } = useAuth();
  const { badges } = useAdminNotifications();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    totalInfluencers: 0,
    totalBrands: 0,
    activeCampaigns: 0,
    totalRewards: 0,
    pendingModeration: 0,
  });
  const [loading, setLoading] = useState(true);

  const loadDashboardMetrics = useCallback(async () => {
    try {
      const [usersRes, influencersRes, brandsRes, campaignsRes, rewardsRes, pendingRes] = await Promise.all([
        supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true }),
        supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true })?.eq('role', 'influencer'),
        supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true })?.eq('role', 'brand'),
        supabase?.from('campaigns')?.select('*', { count: 'exact', head: true })?.eq('status', 'active'),
        supabase?.from('wallet_ledger')?.select('amount')?.eq('ledger_type', 'credit'),
        supabase?.from('transactions')?.select('*', { count: 'exact', head: true })?.eq('status', 'pending'),
      ]);
      const totalRewards = rewardsRes?.data?.reduce((sum, r) => sum + parseFloat(r?.amount || 0), 0) || 0;
      setDashboardData({
        totalUsers: usersRes?.count || 0,
        totalInfluencers: influencersRes?.count || 0,
        totalBrands: brandsRes?.count || 0,
        activeCampaigns: campaignsRes?.count || 0,
        totalRewards,
        pendingModeration: pendingRes?.count || 0,
      });
    } catch (err) {
      console.error('[AdminPanel] loadDashboardMetrics error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardMetrics();
  }, [loadDashboardMetrics]);

  useRealtimeSubscription({
    channelName: 'admin-dashboard-metrics',
    table: 'transactions',
    enabled: true,
    onChange: loadDashboardMetrics,
  });

  useRealtimeSubscription({
    channelName: 'admin-dashboard-users',
    table: 'user_profiles',
    enabled: true,
    onChange: loadDashboardMetrics,
  });

  const handleLogout = async () => {
    await signOut?.();
  };

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || 'Admin';

  const sectionComponents = {
    dashboard: <DashboardOverview dashboardData={dashboardData} loading={loading} onNavigate={setActiveSection} />,
    users: <UserManagement currentAdminId={user?.id} />,
    brands: <BrandManagement currentAdminId={user?.id} />,
    campaigns: <CampaignManagement currentAdminId={user?.id} />,
    moderation: <ModerationQueue currentAdminId={user?.id} />,
    'card-applications': <CardApplicationsReview currentAdminId={user?.id} />,
    rewards: <RewardsLedger currentAdminId={user?.id} />,
    analytics: <AnalyticsReports />,
    notifications: <NotificationsCommunications currentAdminId={user?.id} />,
    settings: <PlatformSettings />,
    audit: <AuditLogs />,
  };

  const sectionTitle = {
    dashboard: 'Dashboard Overview',
    users: 'User Management',
    brands: 'Brand Management',
    campaigns: 'Campaign Management',
    moderation: 'Moderation Queue',
    'card-applications': 'Card Applications Review',
    rewards: 'Rewards & Wallet Ledger',
    analytics: 'Analytics & Reports',
    notifications: 'Notifications & Communications',
    settings: 'Platform Settings',
    audit: 'Security & Audit Logs',
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Toast notifications */}
      <ToastContainer />
      {/* Sidebar */}
      <AdminSidebar
        activeSection={activeSection}
        onNavigate={(section) => { setActiveSection(section); setSidebarOpen(false); }}
        onLogout={handleLogout}
        displayName={displayName}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        badges={badges}
      />
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-[90] lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* Main content */}
      <div className="flex-1 lg:ml-64 min-h-screen flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-card border-b border-border px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted text-foreground transition-colors"
              aria-label="Open menu"
            >
              <Icon name="Menu" size={20} />
            </button>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                {sectionTitle?.[activeSection] || 'Admin Panel'}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-medium text-primary">Live</span>
            </div>
            {/* Notification Bell */}
            <NotificationBell onNavigate={(section) => { setActiveSection(section); }} />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Icon name="User" size={14} color="var(--color-primary)" />
              </div>
              <span className="hidden sm:block text-sm font-medium text-foreground">{displayName}</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          {sectionComponents?.[activeSection] || sectionComponents?.dashboard}
        </main>
      </div>
    </div>
  );
};

const AdminPanel = () => (
  <AdminNotificationProvider>
    <AdminPanelInner />
  </AdminNotificationProvider>
);

export default AdminPanel;